import { ReactNode, useRef, useEffect, useCallback, useState } from 'react';
import {
    HStack,
    VStack,
    HStackProps,
    ButtonProps,
    IconButton,
    Popover,
    PopoverTrigger,
    PopoverContent,
} from '@cad-arc';
import { useDrawerRender } from '../../drawers/components/drawer-context-provider';
import { ResponsiveButton } from '../types';

export function ResponsiveButtons({
    buttons,
    containerProps,
    moreActionsButtonProps,
}: {
    buttons: ResponsiveButton[];
    containerProps?: HStackProps;
    moreActionsButtonProps?: ButtonProps;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const drawerRender = useDrawerRender();
    const [displayedButtons, setDisplayedButtons] = useState<ReactNode[]>([]);
    const [popoverButtons, setPopoverButtons] = useState<ReactNode[]>([]);
    const showMoreActionsPopoverButton = popoverButtons.length > 0;

    const setDisplayedAndPopoverButtons = useCallback(() => {
        const containerWidth = ref.current?.getBoundingClientRect().width ?? Infinity;
        const displayedButtons = [];
        const popoverButtons = [];

        for (const button of buttons) {
            if (containerWidth >= button.breakpoint) {
                displayedButtons.push(button.component);
            } else {
                popoverButtons.unshift(button.component);
            }
        }

        setDisplayedButtons(displayedButtons);
        setPopoverButtons(popoverButtons);
    }, [buttons]);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        setDisplayedAndPopoverButtons();
        window.addEventListener('resize', setDisplayedAndPopoverButtons);

        return () => {
            window.removeEventListener('resize', setDisplayedAndPopoverButtons);
        };
    }, [setDisplayedAndPopoverButtons, drawerRender]);

    return (
        <HStack width="100%" gap="5px" ref={ref} {...containerProps}>
            {displayedButtons.map((button) => button)}
            {showMoreActionsPopoverButton && (
                <Popover>
                    <PopoverTrigger>
                        <IconButton icon="MoreActions" {...moreActionsButtonProps} />
                    </PopoverTrigger>
                    <PopoverContent>
                        <VStack alignItems="center" gap="10px">
                            {popoverButtons.map((button) => button)}
                        </VStack>
                    </PopoverContent>
                </Popover>
            )}
        </HStack>
    );
}
